import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";

import { createQualityRca, fetchQualityCaseRcas, linkQualityRca } from "./qualityRcaApi";

export function QualityCaseRcasCard({
  caseId,
  incidentIds,
}: {
  caseId: number;
  incidentIds: number[];
}): JSX.Element {
  const writesAllowed = useHubModuleWrite("quality");
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [problem, setProblem] = useState("");
  const [rcaIdToLink, setRcaIdToLink] = useState("");
  const [eventId, setEventId] = useState("");

  const listQuery = useQuery({
    queryKey: ["quality", "case", caseId, "rcas"],
    queryFn: () => fetchQualityCaseRcas(caseId),
    enabled: Number.isFinite(caseId) && caseId > 0,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["quality", "case", caseId, "rcas"] });
    await queryClient.invalidateQueries({ queryKey: ["quality", "rcas"] });
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const parsedEvent = eventId.trim() === "" ? null : Number(eventId.trim());
      if (parsedEvent != null && (!Number.isFinite(parsedEvent) || parsedEvent < 1)) {
        throw new Error("Event id must be a positive integer.");
      }
      return createQualityRca({
        problemStatement: problem.trim() || null,
        qualityCaseId: caseId,
        qualityEventId: parsedEvent,
        qualityIncidentIds: incidentIds,
      });
    },
    onSuccess: async (created) => {
      setShowCreate(false);
      setProblem("");
      setEventId("");
      showToast({ tone: "success", message: `Created RCA ${created.rca.id}.` });
      await invalidate();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const linkMutation = useMutation({
    mutationFn: (rcaId: number) => linkQualityRca(rcaId, { qualityCaseId: caseId, qualityIncidentIds: incidentIds }),
    onSuccess: async (linked) => {
      setRcaIdToLink("");
      showToast({ tone: "success", message: `Linked RCA ${linked.rca.id}.` });
      await invalidate();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const rows = listQuery.data?.rows ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center gap-2">
        <CardTitleWithHelp hoverHint="Root cause analyses for this case. Create one here, or link an RCA that already exists. Linked NCRs on this case are attached when you create or link.">
          Linked RCAs
        </CardTitleWithHelp>
        {writesAllowed && !showCreate ? (
          <Button size="sm" variant="secondary" onClick={() => setShowCreate(true)}>
            New RCA
          </Button>
        ) : null}
      </CardHeader>
      <CardBody className="space-y-4">
        {writesAllowed && showCreate ? (
          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div>
              <Label htmlFor={`case-${caseId}-rca-problem`}>Problem statement</Label>
              <TextArea
                id={`case-${caseId}-rca-problem`}
                value={problem}
                rows={3}
                onChange={(event) => setProblem(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor={`case-${caseId}-rca-event`}>Event id (optional)</Label>
              <Input
                id={`case-${caseId}-rca-event`}
                value={eventId}
                onChange={(event) => setEventId(event.target.value)}
              />
            </div>
            <p className="text-xs text-slate-500">
              {incidentIds.length === 0
                ? "No NCRs are linked to this case yet. You can attach them later from the RCA."
                : `This RCA will include NCR ${incidentIds.join(", ")}.`}
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
                Create RCA
              </Button>
            </div>
          </div>
        ) : null}
        {writesAllowed ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1">
              <Label htmlFor={`case-${caseId}-rca-link`}>RCA id</Label>
              <Input
                id={`case-${caseId}-rca-link`}
                value={rcaIdToLink}
                onChange={(event) => setRcaIdToLink(event.target.value)}
                placeholder="Existing RCA id"
              />
            </div>
            <Button
              disabled={linkMutation.isPending || rcaIdToLink.trim() === ""}
              onClick={() => {
                const parsed = Number(rcaIdToLink);
                if (!Number.isFinite(parsed) || parsed < 1 || String(parsed) !== rcaIdToLink.trim()) {
                  showToast({ tone: "error", message: "RCA id must be a positive integer." });
                  return;
                }
                linkMutation.mutate(parsed);
              }}
            >
              Link RCA
            </Button>
          </div>
        ) : null}
        {listQuery.isError ? <p className="text-sm text-red-700">{(listQuery.error as Error).message}</p> : null}
        {rows.length === 0 && !listQuery.isLoading ? (
          <p className="text-sm text-slate-600">No RCAs linked to this case.</p>
        ) : null}
        {rows.length > 0 ? (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2">RCA</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Problem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="px-2 py-2 tabular-nums">
                      <Link className="text-sky-700 hover:underline" to={`/quality/rcas/${row.id}`}>
                        {row.id}
                      </Link>
                    </td>
                    <td className="px-2 py-2">{row.status}</td>
                    <td className="px-2 py-2">{row.problemStatement ?? EM_DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
